import { advanceStage, type UserMemory } from './memory.ts';
import { overallCefr, type Skills } from './skills.ts';
import type { TaskKind } from './tasks.ts';

/**
 * The assessment set (ADR-0012): the kinds of speaking task the level rests on, in the order the journey asks for
 * them (call 1's introduction, then call 2's read-aloud and interview question). Each is answered or skipped once.
 */
export const ASSESSMENT_KINDS = [
  'free_speech',
  'read_aloud',
  'interview_answer',
] as const satisfies readonly TaskKind[];

export type AssessmentEntry = {
  status: 'answered' | 'skipped';
  sessionId: string;
  at: string;
  scored: boolean;
  /** Skipped by the call-2 cap, not by the user. */
  capped?: boolean;
};

/**
 * Records one kind. Pure. The first entry stands, with two exceptions that keep the better evidence: an answer
 * replaces a skip (skipping the read-aloud once shouldn't hide one answered later), and a scored answer replaces an
 * unscored one (a capped or unavailable scorer shouldn't leave the kind without scores for good).
 */
export function recordAssessment(memory: UserMemory, kind: TaskKind, entry: AssessmentEntry): UserMemory {
  const current = memory.assessment.items[kind];
  const better =
    entry.status === 'answered' &&
    (current?.status === 'skipped' || (current?.status === 'answered' && !current.scored && entry.scored));
  if (current && !better) return memory;
  return { ...memory, assessment: { ...memory.assessment, items: { ...memory.assessment.items, [kind]: entry } } };
}

/** Kinds with neither an answer nor a skip yet, in the order they're asked for. */
export function assessmentLeft(memory: UserMemory): TaskKind[] {
  return ASSESSMENT_KINDS.filter((k) => !memory.assessment.items[k]);
}

// Model-facing. `kindsNotYetDone`, not "assessment", which the coach must never say (ADR-0012). No "answers still
// needed" count: in a call-2 eval the coach chased it with extra questions, and a scorer outage would make that endless.
export type Progress = { level: string | null; levelFrom: number; kindsNotYetDone: TaskKind[] };

/**
 * What the coach gets with each answer, from the memory just written: the level once enough answers back it, and what
 * the assessment still needs. The prompt is locked when the call starts, so this is how the model learns it mid-call.
 */
export function progressOf(memory: UserMemory): Progress {
  const samples = memory.skills.cefrHistory.length;
  return {
    level: overallCefr(memory.skills),
    levelFrom: samples,
    kindsNotYetDone: memory.journey.stage === 'assessed' ? [] : assessmentLeft(memory),
  };
}

// A call-2 call that opened an exercise is a try. After CALL2_TRIES of them, what's left counts as skipped, so calls
// that keep dropping can't hold anyone at context_ready.
export const CALL2_TRIES = 2;
// A decline said out loud opens no exercise, so conversations that never reached one count separately, with more room:
// one that dropped during small talk mustn't read as a refusal. After this many, what's left counts as skipped too.
export const CALL2_CALLS = 3;

type Call = { sessionId: string; at: string };

/**
 * Moves context_ready to assessed once every kind is answered or skipped, saving where they started. Pure; a no-op
 * at any other stage or while a kind is left. `call` is the call that completed the set.
 */
export function assessIfComplete(memory: UserMemory, call: Call): UserMemory {
  if (memory.journey.stage !== 'context_ready' || assessmentLeft(memory).length) return memory;
  const assessed = advanceStage(memory, 'context_ready', 'assessed');
  // A baseline from an earlier move (an admin jump back) stays, and so does the call that saved it.
  if (assessed.assessment.baseline) return assessed;
  return withBaseline({ ...assessed, assessment: { ...assessed.assessment, baselineIn: call.sessionId } }, call.at);
}

/**
 * An answer that landed after its call-2 session completed (End tapped during the analysis). The set may be complete
 * only now, or the session already completed it and saved a baseline without this answer's scores. Pure.
 */
export function afterLateAnswer(memory: UserMemory, call: Call): UserMemory {
  if (memory.journey.stage === 'context_ready') return assessIfComplete(memory, call);
  if (memory.assessment.baselineIn === call.sessionId) return withBaseline(memory, call.at, true);
  return memory;
}

function withBaseline(memory: UserMemory, at: string, refresh = false): UserMemory {
  if (memory.assessment.baseline && !refresh) return memory;
  // Nothing was scored (the scorer was capped or down, or every kind was skipped): there's no start to measure from,
  // and a later answer isn't one.
  const baseline = skillsAt(memory.skills, at);
  return baseline ? { ...memory, assessment: { ...memory.assessment, baseline } } : memory;
}

export type SkillsSnapshot = NonNullable<UserMemory['assessment']['baseline']>;

/** The skills as they stand, to compare with later (the baseline, a check-in's result); null when nothing is scored. */
export function skillsAt(s: Skills, at: string): SkillsSnapshot | null {
  const metrics = {
    cefr: overallCefr(s),
    overall: s.overall,
    pronunciation: s.pronunciation,
    fluency: s.fluency,
    grammar: s.grammar,
    vocabulary: s.vocabulary,
  };
  return Object.values(metrics).every((v) => v === null) ? null : { ...metrics, at };
}
